import type {
  LhcFilter,
  LhcItemsFilter,
  SelectOption,
} from '@diamond/shared/types';

export interface LHCListFilterState {
  page: number;
  size: number;
  dateFrom: string;
  dateTo: string;
  plantValue: SelectOption | null;
  cabangValue: SelectOption | null;
  searchQuery: string;
}

const normalizeValue = (value: string): string => value.trim();

const getValidOptionValue = (option: SelectOption | null): string | null => {
  const rawValue = option?.value;
  if (!rawValue) {
    return null;
  }
  const normalizedValue = normalizeValue(rawValue);
  if (!normalizedValue || normalizedValue === 'all') {
    return null;
  }
  return normalizedValue;
};

const DATE_PATTERNS = [
  {
    regex: /^(\d{2})\/(\d{2})\/(\d{4})$/,
    getParts: (match: RegExpExecArray) => ({
      day: Number(match[1]),
      month: Number(match[2]),
      year: Number(match[3]),
    }),
  },
  {
    regex: /^(\d{4})-(\d{2})-(\d{2})$/,
    getParts: (match: RegExpExecArray) => ({
      day: Number(match[3]),
      month: Number(match[2]),
      year: Number(match[1]),
    }),
  },
] as const;

const toUnixSeconds = ({
  day,
  month,
  year,
}: {
  day: number;
  month: number;
  year: number;
}): number | null => {
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  const isValidDate =
    utcDate.getUTCFullYear() === year &&
    utcDate.getUTCMonth() === month - 1 &&
    utcDate.getUTCDate() === day;

  return isValidDate ? Math.floor(utcDate.getTime() / 1000) : null;
};

function parseDateToUnixSeconds(dateValue: string): number | null {
  const trimmedValue = normalizeValue(dateValue);
  if (!trimmedValue) {
    return null;
  }

  const matchedPattern = DATE_PATTERNS.find(({ regex }) =>
    regex.test(trimmedValue)
  );
  if (!matchedPattern) {
    return null;
  }

  const match = matchedPattern.regex.exec(trimmedValue);
  if (!match) {
    return null;
  }

  return toUnixSeconds(matchedPattern.getParts(match));
}

export function buildLhcFilter(state: LHCListFilterState): LhcFilter {
  const startDateUnix = parseDateToUnixSeconds(state.dateFrom);
  const endDateUnix = parseDateToUnixSeconds(state.dateTo);
  const plantId = getValidOptionValue(state.plantValue);
  const distributionChannel = getValidOptionValue(state.cabangValue);
  const trimmedSearchQuery = normalizeValue(state.searchQuery);

  return {
    page: state.page,
    size: state.size,
    ...(startDateUnix !== null && endDateUnix !== null
      ? {
          start_date: startDateUnix,
          end_date: endDateUnix,
        }
      : {}),
    ...(plantId ? { collection_plant_id: plantId } : {}),
    ...(distributionChannel
      ? { distribution_channel: distributionChannel }
      : {}),
    ...(trimmedSearchQuery
      ? {
          search_by: 'lhc_no',
          search_query: trimmedSearchQuery,
        }
      : {}),
  };
}

export interface LHCItemsFilterState {
  dateFrom: string;
  dateTo: string;
  plantFilter: SelectOption | null;
  appliedSearch: string;
}

export function buildLhcItemsFilter(
  state: LHCItemsFilterState
): LhcItemsFilter {
  const startDateUnix = parseDateToUnixSeconds(state.dateFrom);
  const endDateUnix = parseDateToUnixSeconds(state.dateTo);
  const plantId = getValidOptionValue(state.plantFilter);
  const trimmedSearchQuery = normalizeValue(state.appliedSearch);

  return {
    ...(startDateUnix !== null ? { start_date: startDateUnix } : {}),
    ...(endDateUnix !== null ? { end_date: endDateUnix } : {}),
    ...(plantId ? { collection_plant_id: plantId } : {}),
    ...(trimmedSearchQuery ? { search_query: trimmedSearchQuery } : {}),
  };
}
